import type { TFunction } from "i18next";
import { matchPath } from "react-router-dom";

import { paths } from "@/routes/paths";

export interface AdminBreadcrumb {
  label: string;
  to?: string;
}

interface AdminBreadcrumbRoute {
  path: string;
  breadcrumbs: (t: TFunction, params: Record<string, string | undefined>) => AdminBreadcrumb[];
}

const adminBreadcrumbRoutes: AdminBreadcrumbRoute[] = [
  {
    path: `${paths.admin.customers}/notes/:customerId`,
    breadcrumbs: (t) => [
      { label: t("management.customers.customers.title"), to: "/admin/organization/customers" },
      { label: t("management.customers.customerNotes.breadcrumbs.notes") },
    ],
  },
  {
    path: `${paths.admin.stores}/add/:customerId/:customerName`,
    breadcrumbs: (t) => [
      { label: t("management.stores.stores.title"), to: "/admin/organization/stores" },
      { label: t("common.breadcrumbs.create") },
    ],
  },
  {
    path: `${paths.admin.stores}/edit/:storeId`,
    breadcrumbs: (t) => [
      { label: t("management.stores.stores.title"), to: "/admin/organization/stores" },
      { label: t("common.breadcrumbs.edit") },
    ],
  },
  {
    path: `${paths.admin.stores}/:storeId`,
    breadcrumbs: (t) => [
      { label: t("management.stores.stores.title"), to: paths.admin.stores },
      { label: t("common.breadcrumbs.details") },
    ],
  },
  {
    path: `${paths.admin.stores}/edit-rooms/:storeId`,
    breadcrumbs: (t) => [
      { label: t("management.stores.stores.title"), to: "/admin/organization/stores" },
      { label: t("common.breadcrumbs.fittingRooms") },
    ],
  },
  {
    path: `${paths.admin.devices}/history/:deviceId`,
    breadcrumbs: (t, params) => [
      { label: t("nav.devices"), to: "/admin/hardware/devices" },
      { label: params.deviceId ?? "" },
    ],
  },
  {
    path: `${paths.admin.rfidReaders}/add`,
    breadcrumbs: (t) => [
      { label: t("management.devices.rfid.readers.title"), to: "/admin/hardware/rfid-readers" },
      { label: t("common.breadcrumbs.create") },
    ],
  },
  {
    path: `${paths.admin.rfidReaders}/edit/:readerName`,
    breadcrumbs: (t) => [
      { label: t("management.devices.rfid.readers.title"), to: "/admin/hardware/rfid-readers" },
      { label: t("common.breadcrumbs.edit") },
    ],
  },
  {
    path: `${paths.admin.rfidReaders}/:readerName`,
    breadcrumbs: (t) => [
      { label: t("management.devices.rfid.readers.title"), to: "/admin/hardware/rfid-readers" },
      { label: t("common.breadcrumbs.details") },
    ],
  },
  {
    path: `${paths.admin.configs}/edit/:configId`,
    breadcrumbs: (t) => [
      { label: t("operations.configs.configs.title"), to: "/admin/configuration/configs" },
      { label: t("common.breadcrumbs.edit") },
    ],
  },
  {
    path: `${paths.admin.etlRuns}/:jobId/report`,
    breadcrumbs: (t) => [
      { label: t("operations.etl.eTLRuns.title"), to: "/admin/data/etl-runs" },
      { label: t("common.breadcrumbs.report") },
    ],
  },
  {
    path: `${paths.admin.etlRuns}/:jobId`,
    breadcrumbs: (t) => [
      { label: t("operations.etl.eTLRuns.title"), to: "/admin/data/etl-runs" },
      { label: t("common.breadcrumbs.details") },
    ],
  },
  {
    path: `${paths.admin.inventory}/product/:productId`,
    breadcrumbs: (t) => [
      { label: t("inventory.title"), to: "/admin/experimental/inventory" },
      { label: t("inventory.productDetail") },
    ],
  },
  {
    path: `${paths.admin.webhooks}/create`,
    breadcrumbs: (t) => [
      { label: t("management.webhooks.webhooks.title"), to: "/admin/integrations/webhooks" },
      { label: t("common.breadcrumbs.create") },
    ],
  },
  {
    path: `${paths.admin.webhooks}/create/:customerId`,
    breadcrumbs: (t) => [
      { label: t("management.webhooks.webhooks.title"), to: "/admin/integrations/webhooks" },
      { label: t("common.breadcrumbs.create") },
    ],
  },
  {
    path: `${paths.admin.sso}/:customerId/new`,
    breadcrumbs: (t) => [
      { label: "Single sign-on", to: paths.admin.sso },
      { label: t("common.breadcrumbs.create") },
    ],
  },
  {
    path: `${paths.admin.sso}/:customerId/:providerId`,
    breadcrumbs: (t) => [
      { label: "Single sign-on", to: paths.admin.sso },
      { label: t("common.breadcrumbs.edit") },
    ],
  },
  {
    path: `${paths.admin.webhooks}/edit/:webhookId`,
    breadcrumbs: (t) => [
      { label: t("management.webhooks.webhooks.title"), to: "/admin/integrations/webhooks" },
      { label: t("common.breadcrumbs.edit") },
    ],
  },
];

const adminPathPrefixes = [
  paths.admin.commandCenter,
  paths.admin.customers,
  paths.admin.stores,
  paths.admin.devices,
  paths.admin.rfidReaders,
  paths.admin.centralRfidActivity,
  paths.admin.etlRuns,
  paths.admin.dataConsistency,
  paths.admin.shopperDetails,
  paths.admin.webhooks,
  paths.admin.apiKeys,
  paths.admin.sso,
  paths.admin.configs,
  paths.admin.mobileLinks,
  paths.admin.categories,
  paths.admin.simulator,
  paths.admin.inventory,
  paths.admin.comparisonAnalytics,
];

export const getAdminBreadcrumbs = (
  pathname: string,
  title: string,
  t: TFunction,
): AdminBreadcrumb[] | null => {
  const isAdminPath = adminPathPrefixes.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
  if (!isAdminPath) return null;
  const route = adminBreadcrumbRoutes
    .map((entry) => ({ entry, match: matchPath({ path: entry.path, end: true }, pathname) }))
    .find(({ match }) => match);
  return [
    { label: t("nav.sections.craveAdmin") },
    ...(route ? route.entry.breadcrumbs(t, route.match?.params ?? {}) : [{ label: title }]),
  ];
};
